import _ from 'underscore';

import {callAsyncWithDataModifications, callWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import * as NavConstants from 'constants/NavConstants';
import AppDispatcher from 'lib/Dispatcher';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import * as ShortcutUtil from 'util/ShortcutUtil';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';

import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import * as FileBrowserDAO from './FileBrowserDAO';

const FinderActions = {
  _bulkDeleteUsingMethod(deletionMethod, zippedArgs, finderId) {
    let ids;
    let parentFolderIds;
    if (zippedArgs.length === 0) {
      ids = [];
      parentFolderIds = [];
    } else {
      [ids, parentFolderIds] = Array.from(_.unzip(zippedArgs));
    }
    deletionMethod(ids, {parentFolderIds})
      .done(() => {
        const section = NavConstants.FINDER_ID_TO_SECTION[finderId];
        getStore().dispatch(SearchUIReduxActions.refreshListing(section));
        AppDispatcher.dispatch({
          actionType: EventConstants.FILE_BROWSER_ITEMS_DELETED,
          ids,
        });
      })
      .fail((response) => notifications.notifyError(response));
  },

  bulkDelete(finderId, files, {parentFolderIds}) {
    // FIXME: We can have multiple parents (in search results)
    // eslint-disable-next-line no-restricted-syntax
    const ids = _.pluck(files, 'id');
    const shortcutFlags = files.map((file) => ShortcutUtil.shouldShowShortcut(file, finderId));

    // Partition arguments into shortcut and non-shortcut file lists
    const zippedArgs = _.zip(ids, parentFolderIds, shortcutFlags);
    const [zippedShortcutArgs, zippedNonShortcutArgs] = Array.from(
      _(zippedArgs).partition((...args) => {
        const shortcutFlag = Array.from(args[0])[2];
        return shortcutFlag;
      })
    );

    // Delete shortcuts and non-shortcuts independently
    this._bulkDeleteUsingMethod(FileBrowserDAO.deleteFiles, zippedNonShortcutArgs, finderId);
    this._bulkDeleteUsingMethod(FileBrowserDAO.deleteShortcuts, zippedShortcutArgs, finderId);
  },

  async updateTagsWithDataMods(
    ids,
    isRegistered,
    {toSet = {}, toAdd = [], toRemove = [], toRename = {}},
    {onFailure} = {}
  ) {
    let tags = null;
    await callAsyncWithDataModifications({
      changeSummary: isRegistered
        ? getInventoryRegistryChangeSummary({
            apiIds: ids,
            action: InventoryRegistryDataModAction.MODIFY,
          })
        : '',
      onPerformAction: async (dataModsHeader) => {
        tags = await this.updateTags(ids, {toSet, toAdd, toRemove, toRename}, {onFailure}, dataModsHeader);
      },
      apiIds: ids,
      analyticsParams: {
        module: 'REGISTRY',
        actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: ids.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY && isRegistered,
    });
    return tags;
  },

  async updateTags(
    ids,
    {toSet = {}, toAdd = [], toRemove = [], toRename = {}},
    {onFailure} = {},
    dataModsHeader
  ) {
    try {
      const data = await FileBrowserDAO.updateTags(
        ids,
        {
          toSet,
          toAdd,
          toRemove,
          toRename,
        },
        dataModsHeader
      );
      AppDispatcher.dispatch({
        actionType: EventConstants.FILE_TAGS_UPDATED,
        tagData: data.data,
      });
      notifications.success('Fields were updated successfully.');
      return data.data;
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      notifications.notifyError(error);
      if (onFailure) {
        onFailure();
      }
    }
    return null;
  },

  updateTagsWithFinderWithDataMods(finderId, ids, hasRegisteredItems, {toSet, toAdd, toRemove, toRename}) {
    callWithDataModifications({
      changeSummary: hasRegisteredItems
        ? getInventoryRegistryChangeSummary({
            apiIds: ids,
            action: InventoryRegistryDataModAction.MODIFY,
          })
        : '',
      onPerformAction: (dataModsHeader) => {
        this.updateTagsWithFinder(finderId, ids, {toSet, toAdd, toRemove, toRename}, dataModsHeader);
      },
      apiIds: ids,
      analyticsParams: {
        module: 'REGISTRY',
        actionOrigin: ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: ids.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic:
        BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_REGISTRY && hasRegisteredItems,
    });
  },

  // TODO(T6458): make passing the finderId the default behavior in updateTags and get rid of this function
  // Updates the finder with updated values, allowing for UI refreshes of new info without a refetch
  updateTagsWithFinder(finderId, ids, {toSet, toAdd, toRemove, toRename}, dataModsHeader) {
    FileBrowserDAO.updateTags(ids, {toSet, toAdd, toRemove, toRename}, dataModsHeader)
      .then(({data}) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.FILE_TAGS_WITH_FINDER_UPDATED,
          finderId,
          tagData: data,
        });

        const fileCacheUpdates = data
          .map((fileWithTags) => {
            const {id, tags} = fileWithTags;
            const cachedBioentity = readFragmentFromCache(getFragmentWrapper('BioentitySummary'), id);
            return cachedBioentity ? {...cachedBioentity, tagsJson: tags} : null;
          })
          .filter((cacheUpdate) => cacheUpdate != null);
        writeFragmentsToCache(getFragmentWrapper('BioentitySummary'), fileCacheUpdates);

        notifications.success('Fields were updated successfully.');
      })
      .catch(notifications.notifyError);
  },
};

export default FinderActions;
